import {
	BoundingSphere,
	Cartesian3,
	Cartographic,
	type Cesium3DTileset,
	HeadingPitchRoll,
	Math as CesiumMath,
	Matrix3,
	Matrix4,
	OrientedBoundingBox,
	Transforms,
} from 'cesium';

/** Limits of the box along its own axes, in metres from its centre. */
export type ClipBounds = {
	xMin: number;
	xMax: number;
	yMin: number;
	yMax: number;
	zMin: number;
	zMax: number;
};

/** Turn of the box against the local east-north-up frame, in degrees. */
export type ClipRotation = { heading: number; pitch: number; roll: number };

/**
 * A limit box in world coordinates: `center` is ECEF, the box axes are
 * `ENU(center) * HPR(rotation)` and `bounds` are measured along them. Nothing here
 * depends on the loaded layers, so a layer that loads later cannot move the box.
 */
export type ClipBox = {
	center: [number, number, number];
	rotation: ClipRotation;
	bounds: ClipBounds;
};

/** The properties TilesLoader puts on the tilesets it adds as layers. */
export type LayerTileset = Cesium3DTileset & {
	uniqueId?: string;
	name?: string;
	isSplat?: boolean;
};

// Real models sit within a few kilometres of the ellipsoid. A model without a
// georeference sits at the Earth's centre, and raw projected (e.g. UTM)
// coordinates read as ECEF land hundreds of kilometres below the surface.
const MAX_GEOREFERENCED_HEIGHT = 20_000;
const MARGIN_RATIO = 0.05;
const MIN_MARGIN = 0.5;

export const isGeoreferenced = (center: Cartesian3): boolean => {
	const cartographic = Cartographic.fromCartesian(center);
	return cartographic !== undefined && Math.abs(cartographic.height) <= MAX_GEOREFERENCED_HEIGHT;
};

/** The level cube around a sphere. */
const levelCube = ({ center, radius }: BoundingSphere): OrientedBoundingBox => {
	const enu = Matrix4.getMatrix3(Transforms.eastNorthUpToFixedFrame(center), new Matrix3());
	return new OrientedBoundingBox(center, Matrix3.multiplyByScalar(enu, radius, new Matrix3()));
};

/** The world-space oriented box of a tileset's root, or the box around its sphere. */
export const tilesetVolume = (tileset: Cesium3DTileset): OrientedBoundingBox => {
	// Bounding boxes and regions expose an OrientedBoundingBox here, spheres a BoundingSphere.
	// The tile's bounding volume is not in the typings.
	const root = tileset.root as unknown as { boundingVolume?: { boundingVolume?: unknown } } | undefined;
	const volume = root?.boundingVolume?.boundingVolume;
	if (volume instanceof OrientedBoundingBox) return volume;
	return levelCube(tileset.boundingSphere);
};

type TileNode = { children?: TileNode[]; boundingVolume?: { boundingVolume?: unknown } };

/**
 * The bounding volumes of a layer's tiles: single children are followed from the root down to
 * the first tile with two or more children, whose children are taken (the converter's tile
 * grid). A layer with a single tile gives its root volume. The converter writes axis-aligned
 * boxes, so the grid shows a diagonal model's direction where the root box does not.
 */
export const layerPieces = (tileset: Cesium3DTileset): OrientedBoundingBox[] => {
	// The tile tree is not in the typings.
	let node = tileset.root as unknown as TileNode | undefined;
	while (node?.children?.length === 1) node = node.children[0];
	const pieces = (node?.children ?? []).flatMap((child) => {
		const volume = child.boundingVolume?.boundingVolume;
		if (volume instanceof OrientedBoundingBox) return [volume];
		if (volume instanceof BoundingSphere) return [levelCube(volume)];
		return [];
	});
	return pieces.length >= 2 ? pieces : [tilesetVolume(tileset)];
};

// A spread whose larger variance is at least this many times the smaller one has a direction.
const MIN_ELONGATION = 1.5;

/** Folds a heading into (-90°, 90°]; an axis has no direction. No -0: it reads back from storage as 0. */
const foldHeading = (degrees: number) => {
	let heading = degrees;
	while (heading <= -90) heading += 180;
	while (heading > 90) heading -= 180;
	return heading === 0 ? 0 : heading;
};

/** Heading of the main horizontal axis of the points, in ENU at `origin`; undefined when they have none. */
export const principalHeading = (points: Cartesian3[], origin: Cartesian3): number | undefined => {
	if (points.length < 2) return undefined;
	const toEnu = Matrix4.inverseTransformation(Transforms.eastNorthUpToFixedFrame(origin), new Matrix4());
	const local = points.map((point) => Matrix4.multiplyByPoint(toEnu, point, new Cartesian3()));
	const meanX = local.reduce((sum, p) => sum + p.x, 0) / local.length;
	const meanY = local.reduce((sum, p) => sum + p.y, 0) / local.length;
	let cxx = 0;
	let cyy = 0;
	let cxy = 0;
	for (const p of local) {
		cxx += (p.x - meanX) ** 2;
		cyy += (p.y - meanY) ** 2;
		cxy += (p.x - meanX) * (p.y - meanY);
	}
	const spread = Math.hypot(cxx - cyy, 2 * cxy);
	const major = (cxx + cyy + spread) / 2;
	const minor = (cxx + cyy - spread) / 2;
	if (major <= 0 || major < MIN_ELONGATION * minor) return undefined;
	// The angle turns counter-clockwise from east; a heading turns clockwise.
	return foldHeading(-CesiumMath.toDegrees(0.5 * Math.atan2(2 * cxy, cxx - cyy)));
};

/**
 * Whether two oriented boxes overlap or are at most `tolerance` metres apart: no axis among
 * the 15 of the separating axis theorem holds them further apart than that.
 */
export const volumesTouch = (a: OrientedBoundingBox, b: OrientedBoundingBox, tolerance: number): boolean => {
	const halvesA = [0, 1, 2].map((i) => Matrix3.getColumn(a.halfAxes, i, new Cartesian3()));
	const halvesB = [0, 1, 2].map((i) => Matrix3.getColumn(b.halfAxes, i, new Cartesian3()));
	const axes = [...halvesA, ...halvesB];
	for (const u of halvesA) for (const v of halvesB) axes.push(Cartesian3.cross(u, v, new Cartesian3()));

	const between = Cartesian3.subtract(b.center, a.center, new Cartesian3());
	const reach = (halves: Cartesian3[], unit: Cartesian3) =>
		halves.reduce((sum, half) => sum + Math.abs(Cartesian3.dot(half, unit)), 0);
	for (const axis of axes) {
		const length = Cartesian3.magnitude(axis);
		// Parallel edges and flat boxes give no axis.
		if (length < 1e-9) continue;
		const unit = Cartesian3.divideByScalar(axis, length, new Cartesian3());
		if (Math.abs(Cartesian3.dot(between, unit)) > reach(halvesA, unit) + reach(halvesB, unit) + tolerance) {
			return false;
		}
	}
	return true;
};

/** Indices of the volumes joined to the anchor through touching volumes, the anchor first. */
export const clusterAround = (anchor: number, volumes: OrientedBoundingBox[], tolerance: number): number[] => {
	const members = [anchor];
	const rest = new Set(volumes.keys());
	rest.delete(anchor);
	for (let next = 0; next < members.length; next++) {
		const current = volumes[members[next]];
		for (const index of [...rest]) {
			if (volumesTouch(current, volumes[index], tolerance)) {
				members.push(index);
				rest.delete(index);
			}
		}
	}
	return members;
};

export const boxFrame = (box: Pick<ClipBox, 'center' | 'rotation'>, result = new Matrix4()): Matrix4 => {
	const enu = Transforms.eastNorthUpToFixedFrame(Cartesian3.fromArray(box.center));
	const { heading, pitch, roll } = box.rotation;
	const turn = Matrix3.fromHeadingPitchRoll(
		new HeadingPitchRoll(
			CesiumMath.toRadians(heading),
			CesiumMath.toRadians(pitch),
			CesiumMath.toRadians(roll)
		)
	);
	return Matrix4.multiply(enu, Matrix4.fromRotationTranslation(turn, Cartesian3.ZERO), result);
};

export const boxBoundingSphere = (box: ClipBox): BoundingSphere => {
	const { xMin, xMax, yMin, yMax, zMin, zMax } = box.bounds;
	const middle = new Cartesian3((xMin + xMax) / 2, (yMin + yMax) / 2, (zMin + zMax) / 2);
	const center = Matrix4.multiplyByPoint(boxFrame(box), middle, new Cartesian3());
	const radius = 0.5 * Math.hypot(xMax - xMin, yMax - yMin, zMax - zMin);
	return new BoundingSphere(center, radius);
};

/**
 * Heading that lines the box's x axis up with the layer's longest horizontal axis,
 * so a model lying diagonally gets a diagonal box instead of a larger square.
 */
const dominantHeading = (volume: OrientedBoundingBox, toEnu: Matrix4): number => {
	let longest = 0;
	let angle = 0;
	for (let i = 0; i < 3; i++) {
		const axis = Matrix3.getColumn(volume.halfAxes, i, new Cartesian3());
		const local = Matrix4.multiplyByPointAsVector(toEnu, axis, new Cartesian3());
		const length = Math.hypot(local.x, local.y);
		if (length > longest) {
			longest = length;
			angle = Math.atan2(local.y, local.x);
		}
	}
	// `angle` turns counter-clockwise from east; a heading turns clockwise.
	return foldHeading(-CesiumMath.toDegrees(angle));
};

/**
 * A level box around the given volumes, centred on them and turned along their spread (or with
 * the largest one when they have no direction), with a small margin so the model is not cut at
 * its outer surface.
 */
export const fitBox = (volumes: OrientedBoundingBox[]): ClipBox | null => {
	if (volumes.length === 0) return null;

	const spheres = volumes.map((volume) => BoundingSphere.fromOrientedBoundingBox(volume));
	const combined = spheres.reduce((union, sphere) => BoundingSphere.union(union, sphere, new BoundingSphere()));
	const center: ClipBox['center'] = [combined.center.x, combined.center.y, combined.center.z];

	const largest = volumes[spheres.indexOf(spheres.reduce((a, b) => (b.radius > a.radius ? b : a)))];
	const toEnu = Matrix4.inverseTransformation(Transforms.eastNorthUpToFixedFrame(combined.center), new Matrix4());
	const corners = volumes.flatMap((volume) => OrientedBoundingBox.computeCorners(volume));

	const fitAt = (heading: number) => {
		const rotation = { heading, pitch: 0, roll: 0 };
		const toBox = Matrix4.inverseTransformation(boxFrame({ center, rotation }), new Matrix4());
		const min = new Cartesian3(Infinity, Infinity, Infinity);
		const max = new Cartesian3(-Infinity, -Infinity, -Infinity);
		for (const corner of corners) {
			const local = Matrix4.multiplyByPoint(toBox, corner, new Cartesian3());
			Cartesian3.minimumByComponent(min, local, min);
			Cartesian3.maximumByComponent(max, local, max);
		}
		return { rotation, min, max, footprint: (max.x - min.x) * (max.y - min.y) };
	};

	// The pieces' spread shows a diagonal model's direction (the converter's boxes themselves are
	// not turned), but a few pieces or nearly coincident centres give a direction by chance; the
	// heading with the smaller footprint wins.
	const headings = [
		principalHeading(
			volumes.map((volume) => volume.center),
			combined.center,
		),
		dominantHeading(largest, toEnu),
	].filter((heading): heading is number => heading !== undefined);
	const { rotation, min, max } = headings.map(fitAt).reduce((best, fit) => (fit.footprint < best.footprint ? fit : best));

	const margin = (low: number, high: number) => Math.max((high - low) * MARGIN_RATIO, MIN_MARGIN);
	const mx = margin(min.x, max.x);
	const my = margin(min.y, max.y);
	const mz = margin(min.z, max.z);

	return {
		center,
		rotation,
		bounds: {
			xMin: min.x - mx,
			xMax: max.x + mx,
			yMin: min.y - my,
			yMax: max.y + my,
			zMin: min.z - mz,
			zMax: max.z + mz,
		},
	};
};

/** Whether the point lies inside the box. */
export const pointInBox = (box: ClipBox, point: Cartesian3): boolean => {
	const local = Matrix4.multiplyByPoint(
		Matrix4.inverseTransformation(boxFrame(box), new Matrix4()),
		point,
		new Cartesian3()
	);
	const { xMin, xMax, yMin, yMax, zMin, zMax } = box.bounds;
	return (
		local.x >= xMin && local.x <= xMax && local.y >= yMin && local.y <= yMax && local.z >= zMin && local.z <= zMax
	);
};

/**
 * How far along the ray from `origin` (unit `direction`) the box begins: 0 from inside it,
 * undefined when the ray misses it or the box lies behind the origin.
 */
export const rayEntryDistance = (box: ClipBox, origin: Cartesian3, direction: Cartesian3): number | undefined => {
	const toBox = Matrix4.inverseTransformation(boxFrame(box), new Matrix4());
	const start = Matrix4.multiplyByPoint(toBox, origin, new Cartesian3());
	const along = Matrix4.multiplyByPointAsVector(toBox, direction, new Cartesian3());
	const { xMin, xMax, yMin, yMax, zMin, zMax } = box.bounds;
	let near = 0;
	let far = Number.POSITIVE_INFINITY;
	for (const [s, d, min, max] of [
		[start.x, along.x, xMin, xMax],
		[start.y, along.y, yMin, yMax],
		[start.z, along.z, zMin, zMax],
	]) {
		if (Math.abs(d) < 1e-12) {
			if (s < min || s > max) return undefined;
			continue;
		}
		const t1 = (min - s) / d;
		const t2 = (max - s) / d;
		near = Math.max(near, Math.min(t1, t2));
		far = Math.min(far, Math.max(t1, t2));
		if (near > far) return undefined;
	}
	return near;
};
